import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { IApplicationState } from "src/spintr/reducer";
import "./ReactionPicker.scss";
import { IReactionPickerConfig } from "src/ui/reducer";
import { likeTypes } from "../types";
import { Label, setReactionPickerConfig, UnstyledButton } from "src/ui";
import { Player } from "@lottiefiles/react-lottie-player";
import { localize } from "src/l10n";
import { mixpanelTrack } from "src/utils";
import { addLike, removeLike } from "../actions";
import { SpintrTypes } from "src/typings";
import { addReaction, removeReaction } from "src/chat";
import { Style } from "src/ui/helpers";
import { IInteractionsState } from "../reducer";
import { IActiveUserProfile } from "src/profile/reducer";
import { Dispatch } from "redux";

interface IProps {
    config?: IReactionPickerConfig;
    allInteractions?: IInteractionsState;
    dispatch?: Dispatch;
    currentUser?: IActiveUserProfile;
    isSmallViewMode?: boolean;
    messages?: Spintr.IChatMessage[];
}

const ReactionPicker: React.FC<IProps> = (props): ReactNode => {
    const {
        config,
        allInteractions,
        dispatch,
        currentUser,
        isSmallViewMode,
        messages
    } = props;

    const [pos, setPos] = useState<DOMRect | undefined>(undefined);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef<boolean>(false);

    useEffect(() => {
        isMountedRef.current = true;

        const handleClickOutside = (event: MouseEvent) => {
            if (!isMountedRef.current) return;

            if (
                isSmallViewMode &&
                config &&
                config.isVisible &&
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                dispatch(setReactionPickerConfig(undefined));
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            isMountedRef.current = false;
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [config, isSmallViewMode, dispatch]);

    const hasLiked = useCallback((likers: Spintr.IUser[]) => {
        if (!isMountedRef.current || !config) return false;

        return config.identity
            ? likers.some(user => user.id === config.identity.id)
            : likers.some(user => user.id === currentUser.id);
    }, [config, currentUser]);

    const addChatMessageReaction = (likeType: number) => {
        if (!config) return;

        const message = messages?.find(x => x.id === config.uberId);
        if (!message) return;

        const reactionExist = message.reactions.find(r =>
            r.likeType === likeType &&
            r.user.id === config.identity.id
        );

        if (reactionExist) {
            dispatch(removeReaction(message.id, likeType, config.identity.id));
        } else {
            dispatch(addReaction(
                message.id,
                likeType,
                config.identity.id,
                config.identity.name
            ));
        }
    };

    const addLikeHandler = (likeType: number) => {
        if (!config) return;

        const interactions = allInteractions?.[config.uberId];
        if (!interactions || interactions.likers === undefined) return;

        const likers: Spintr.IUser[] = interactions.likers || [];
        const alreadyLiked = hasLiked(likers);

        let currentLikeType = 0;

        if (alreadyLiked) {
            const currentUserLike = likers.find((l) => l.id === currentUser.id);
            if (currentUserLike) {
                currentLikeType = currentUserLike.likeType || 0;
            }
        }

        if (alreadyLiked && likeType === currentLikeType) {
            dispatch(removeLike(
                config.uberId,
                { ...currentUser, likeType },
                config.identity
            ));
        } else {
            dispatch(addLike(
                config.uberId,
                { ...currentUser, likeType },
                config.identity
            ));

            if (config.sendToMixpanel) {
                mixpanelTrack("Like");
            }
        }
    };

    const onLikeIconClick = (likeType: number) => {
        if (!isMountedRef.current || !config) return;

        if (config.isChat) {
            addChatMessageReaction(likeType);
        } else {
            addLikeHandler(likeType);
        }

        dispatch(setReactionPickerConfig(undefined));
    };

    const updatePosition = useCallback(() => {
        if (!isMountedRef.current || !config) return;

        if (config.elementId) {
            const el = document.getElementById(config.elementId);
            if (!el) return;

            const rect = el.getBoundingClientRect();
            setPos(rect);
        } else {
            setPos(undefined);
        }
    }, [config]);

    useEffect(() => {
        updatePosition();
    }, [config?.elementId, config?.isVisible, updatePosition]);

    const onMouseLeave = () => {
        dispatch(setReactionPickerConfig(undefined));
    };

    const getXPosition = () => {
        if (!pos || !config) return -2000;

        const x = pos.x + config.xOffset;
        // The row's real width, so every reaction stays on screen (SPI-3562)
        const estimatedPickerWidth = wrapperRef.current?.offsetWidth || 224;

        if (x < 0) {
            return Style.getSpacing(4);
        } else if ((x + estimatedPickerWidth) > document.body.clientWidth) {
            return document.body.clientWidth - estimatedPickerWidth - Style.getSpacing(4);
        }

        return x;
    };

    const playerRefs = useRef<Record<number, Player>>({});

    useEffect(() => {
        if (!config?.isVisible) {
            Object.values(playerRefs.current).forEach(player => {
                player?.stop();
            });
            return;
        }

        Object.values(playerRefs.current).forEach(player => {
            player?.stop();
            player?.play();
        });
    }, [config?.isVisible]);

    const classNames = ["ReactionPicker"];
    if (!config || !config.isVisible) {
        classNames.push("hidden");
    }

    return (
        <div
            ref={wrapperRef}
            onMouseLeave={onMouseLeave}
            className={classNames.join(" ")}
            style={{
                top: !pos || !config
                    ? -2000
                    : (pos.y + window.scrollY + config.yOffset),
                left: getXPosition()
            }}
        >
            {likeTypes.map((lt) => (
                <UnstyledButton
                    className={"LikeType" + lt.id}
                    key={lt.id}
                    onClick={() => onLikeIconClick(lt.id)}
                >
                    <div className="reaction-tooltip">
                        <Label color="white" size="small-1">
                            {localize(lt.name, true)}
                        </Label>
                    </div>
                    <Player
                        ref={(el) => {
                                if (el) {
                                    playerRefs.current[lt.id] = el;
                                } else {
                                    delete playerRefs.current[lt.id];
                                }
                            }}
                        style={{ height: "100%", width: "100%" }}
                        src={lt.json}
                        autoplay={false}
                        loop
                    />
                </UnstyledButton>
            ))}
        </div>
    );
};

export default connect(
    (state: IApplicationState, props: IProps): IProps => ({
        ...props,
        config: state.ui.reactionPickerConfig,
        allInteractions: state.interactions,
        currentUser: state.profile.active,
        isSmallViewMode: state.ui.viewMode <= SpintrTypes.ViewMode.PhoneLandscape,
        messages: state.chat.messages.items
    })
)(ReactionPicker);